'use client';

import { useRef, useState } from 'react';
import { gsap, EASE_OUT } from '@/components/sections/kit/motion';
import type { CompareDossierData } from './types';
import { CONTAINER } from './bits';
import DossierHead from './DossierHead';
import { useChoreo } from './motion';

/**
 * §08 — switching FAQ. Hairline-stacked accordion, mono 01–07 indices,
 * single-open (first open on load), smooth height collapse. The last answer
 * ends with the page's one plain recommendation, brand-bold (the conversion
 * ask lives inside educational content, not a banner).
 */
export default function SwitchingFaq({ data }: { data: CompareDossierData }) {
  const rootRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(0);

  useChoreo(rootRef, (root) => {
    return gsap.from(root.querySelectorAll('[data-faq-item]'), {
      y: 16,
      opacity: 0,
      duration: 0.55,
      ease: EASE_OUT,
      stagger: 0.06,
      paused: true,
    });
  });

  return (
    <section ref={rootRef} className="pt-16 md:pt-24">
      <div className={CONTAINER}>
        <DossierHead
          num="08"
          label="SWITCHING FAQ"
          title="The questions people actually ask mid-switch."
        />
        <div className="mt-8 flex max-w-[880px] flex-col gap-px overflow-hidden rounded-ref-lg border border-line-2 bg-line-2 md:mt-10">
          {data.faqs.map((faq, i) => {
            const isOpen = open === i;
            return (
              <div key={faq.q} data-faq-item className="bg-surface">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`faq-body-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="flex w-full cursor-pointer items-baseline gap-4 px-5 py-[18px] text-left focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-600 md:px-6"
                >
                  <span className="w-[22px] flex-none font-jetbrains text-[11px] text-brand-600">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-[15px] font-semibold leading-[1.45] text-ink">
                    {faq.q}
                  </span>
                  <span
                    aria-hidden="true"
                    className="ml-auto flex-none font-jetbrains text-[14px] text-dim"
                  >
                    {isOpen ? '–' : '+'}
                  </span>
                </button>
                <div
                  id={`faq-body-${i}`}
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="max-w-[760px] px-5 pb-5 pl-[58px] text-[14.5px] leading-[1.65] text-pretty text-muted md:px-6 md:pl-[62px]">
                      {faq.a}
                      {faq.pick && (
                        <span className="font-bold text-brand-600">{faq.pick}</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
